import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage } from '../../util/reactIntl';
import { ExternalLink, Logo, NamedLink } from '../../components';

import css from './LinkedLogo.module.css';

/**
 * This component returns a clickable logo.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.logoClassName andd more style rules in addtion to css.logo
 * @param {string?} props.logoImageClassName overwrite components own css.root
 * @param {('desktop' | 'mobile')} props.layout
 * @param {Object} props.linkToExternalSite
 * @param {string} props.linkToExternalSite.href
 * @param {string?} props.alt alt text for logo image
 * @param {boolean?} props.showBrandName show marketplace name next to the logo
 * @returns {JSX.Element} linked logo component
 */
const LinkedLogo = props => {
  const {
    id,
    className,
    rootClassName,
    logoClassName,
    logoImageClassName,
    layout = 'desktop',
    linkToExternalSite,
    alt,
    showBrandName = false,
    ...rest
  } = props;
  const config = useConfiguration();
  const marketplaceName = config?.marketplaceName;
  const classes = classNames(rootClassName || css.root, className);

  const logo = (
    <Logo
      layout={layout}
      className={classNames(showBrandName && css.brandLogo, logoClassName)}
      logoImageClassName={logoImageClassName}
      alt={showBrandName ? '' : alt}
    />
  );

  const content = showBrandName ? (
    <span className={css.brand}>
      {logo}
      <span className={css.brandName}>
        <FormattedMessage
          id="Topbar.brandName"
          values={{
            marketplaceName,
            accent: chunks => <span className={css.brandAccent}>{chunks}</span>,
          }}
        />
      </span>
    </span>
  ) : (
    logo
  );

  // Note: href might come as an empty string (falsy), in which case we default to 'LandingPage'.
  return linkToExternalSite?.href ? (
    <ExternalLink className={classes} href={linkToExternalSite.href} target="_self" {...rest}>
      {content}
    </ExternalLink>
  ) : (
    <NamedLink id={id} className={classes} name="LandingPage" {...rest}>
      {content}
    </NamedLink>
  );
};

export default LinkedLogo;
